import React from 'react';
import { formatDate } from '../utils/dateFormatter';

interface ChartData {
  label: string;
  value: number;
  color?: string;
}

interface PieChartProps {
  data: ChartData[];
  size?: number;
  donut?: boolean;
}

export const PieChart: React.FC<PieChartProps> = ({ data, size = 150, donut = true }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulativeAngle = 0;

  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={size / 2} fill="#e0e0e0" />
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          fill="#666"
          fontSize="12"
        >
          No data
        </text>
      </svg>
    );
  }

  const center = size / 2;
  const radius = size / 2;
  const holeRadius = donut ? radius * 0.6 : 0;

  // Default colors if not provided
  const defaultColors = [
    '#008060', // accent
    '#f49342', // warning
    '#d82c0d', // error
    '#2c6ecb', // interactive
    '#5c6ac4',
    '#47c1bf',
  ];

  const slices = data.map((item, index) => {
    const angle = (item.value / total) * 360;
    const startAngle = cumulativeAngle;
    cumulativeAngle += angle;

    const startRad = (startAngle - 90) * (Math.PI / 180);
    const endRad = (cumulativeAngle - 90) * (Math.PI / 180);

    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);

    const x3 = center + holeRadius * Math.cos(endRad);
    const y3 = center + holeRadius * Math.sin(endRad);
    const x4 = center + holeRadius * Math.cos(startRad);
    const y4 = center + holeRadius * Math.sin(startRad);

    // SVG path command
    const largeArcFlag = angle > 180 ? 1 : 0;
    
    let pathData;
    if (donut) {
      pathData = [
        `M ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        `L ${x3} ${y3}`,
        `A ${holeRadius} ${holeRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
        `Z`
      ].join(' ');
    } else {
      pathData = [
        `M ${center} ${center}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        `Z`
      ].join(' ');
    }
    
    // Si es 100%, dibujar un círculo completo
    if (data.length === 1 || angle === 360) {
      if (donut) {
         return (
           <path
             key={index}
             d={`M ${center} ${center - radius} 
                A ${radius} ${radius} 0 1 1 ${center} ${center + radius} 
                A ${radius} ${radius} 0 1 1 ${center} ${center - radius}
                M ${center} ${center - holeRadius}
                A ${holeRadius} ${holeRadius} 0 1 0 ${center} ${center + holeRadius}
                A ${holeRadius} ${holeRadius} 0 1 0 ${center} ${center - holeRadius}`}
             fill={item.color || defaultColors[index % defaultColors.length]}
           />
         );
      } else {
        return (
          <circle
            key={index}
            cx={center}
            cy={center}
            r={radius}
            fill={item.color || defaultColors[index % defaultColors.length]}
          />
        );
      }
    }

    return (
      <path
        key={index}
        d={pathData}
        fill={item.color || defaultColors[index % defaultColors.length]}
      />
    );
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {slices}
        {donut && (
          <text
            x="50%"
            y="50%"
            dominantBaseline="middle"
            textAnchor="middle"
            style={{ fontSize: '1.5rem', fontWeight: 'bold', fill: 'var(--text-primary)' }}
          >
            {total}
          </text>
        )}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {data.map((item, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '2px',
                backgroundColor: item.color || defaultColors[index % defaultColors.length],
              }}
            />
            <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              {item.label}: <strong>{item.value}</strong>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface BarChartProps {
  data: ChartData[];
  height?: number;
  width?: string;
  barColor?: string;
}

export const BarChart: React.FC<BarChartProps> = ({ 
  data, 
  height = 200, 
  width = '100%', 
  barColor = 'var(--accent)' 
}) => {
  if (data.length === 0) {
    return (
      <div style={{ height, width, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-tertiary)', borderRadius: '8px' }}>
        <span style={{ color: 'var(--text-secondary)' }}>No hay datos</span>
      </div>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.value));

  return (
    <div style={{ height, width, display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
      {data.map((item, index) => {
        const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
        return (
          <div
            key={index}
            style={{
              flex: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'center',
              position: 'relative',
            }}
            title={`${item.label}: ${item.value}`}
          >
            <div
              style={{
                width: '80%',
                height: `${percentage}%`,
                backgroundColor: item.color || barColor,
                borderRadius: '4px 4px 0 0',
                transition: 'height 0.3s ease',
                position: 'relative',
                minHeight: percentage > 0 ? '4px' : '0',
              }}
            >
              {percentage > 10 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '4px',
                    left: '0',
                    right: '0',
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    color: '#fff',
                    fontWeight: 600,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                  }}
                >
                  {item.value}
                </span>
              )}
            </div>
            <div
              style={{
                marginTop: '8px',
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                textAlign: 'center',
              }}
            >
              {item.label}
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface LineChartProps {
  data: Array<{
    date: string;
    value: number;
  }>;
  height?: number;
  width?: string;
  lineColor?: string;
  fillColor?: string;
  showPoints?: boolean;
}

export const LineChart: React.FC<LineChartProps> = ({ 
  data, 
  height = 200, 
  width = '100%',
  lineColor = 'var(--accent)',
  fillColor = 'rgba(0, 128, 96, 0.1)',
  showPoints = true
}) => {
  if (data.length === 0) {
    return (
      <div style={{ height, width, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-tertiary)', borderRadius: '8px' }}>
        <span style={{ color: 'var(--text-secondary)' }}>No hay datos</span>
      </div>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.value));
  const minValue = Math.min(...data.map((d) => d.value));
  const range = maxValue - minValue || 1;
  const padding = 20;
  const chartHeight = height - padding * 2;

  // Generar puntos para la línea
  const points = data.map((item, index) => {
    const x = (index / (data.length - 1 || 1)) * 100;
    const y = 100 - ((item.value - minValue) / range) * 100;
    return { x, y, value: item.value, date: item.date };
  });

  // Crear path para la línea
  const pathData = points.map((point, index) => {
    return `${index === 0 ? 'M' : 'L'} ${point.x}% ${point.y}%`;
  }).join(' ');

  // Crear path para el área rellena
  const areaPath = `${pathData} L ${points[points.length - 1].x}% 100% L ${points[0].x}% 100% Z`;

  return (
    <div style={{ height, width, position: 'relative', padding: `${padding}px 0` }}>
      <svg width="100%" height={chartHeight} style={{ position: 'absolute', top: padding, left: 0 }}>
        {/* Área rellena */}
        <path
          d={areaPath}
          fill={fillColor}
          style={{ transition: 'all 0.3s ease' }}
        />
        {/* Línea */}
        <path
          d={pathData}
          fill="none"
          stroke={lineColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ transition: 'all 0.3s ease' }}
        />
        {/* Puntos */}
        {showPoints && points.map((point, index) => (
          <circle
            key={index}
            cx={`${point.x}%`}
            cy={`${point.y}%`}
            r="4"
            fill={lineColor}
            style={{ transition: 'all 0.3s ease', cursor: 'pointer' }}
          >
            <title>{`${point.date}: ${point.value}`}</title>
          </circle>
        ))}
      </svg>
      {/* Etiquetas de valores */}
      <div style={{ 
        position: 'absolute', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        display: 'flex', 
        justifyContent: 'space-between',
        fontSize: '0.7rem',
        color: 'var(--text-secondary)',
        padding: '0 4px'
      }}>
        {points.length > 0 && (
          <>
            <span>{formatDate(points[0].date).split('/').slice(0, 2).join('/')}</span>
            {points.length > 1 && <span>{formatDate(points[points.length - 1].date).split('/').slice(0, 2).join('/')}</span>}
          </>
        )}
      </div>
    </div>
  );
};

